import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FileText, Building, User, Grid3x3, Calculator, Download, Pencil, Trash2, X,
  ShieldCheck, Timer, Lock, History, AlertCircle, Loader2, ArrowLeft,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { apiClient } from "@/lib/api";
import { translateKey } from "@/i18n/translationKeys";
import { CategoryColors } from "@/lib/colors";
import { isAdmin } from "@/lib/roles";
import { fmtMoney, cleanNum, normalizeCurrency } from "@/lib/money";
import { PageLoader, Card, EmptyState } from "@/components/shared/ui";

const Field = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-slate-50 py-2.5 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-900">{value}</span>
  </div>
);

export default function EstimationDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const admin = isAdmin(user);

  const [estimation, setEstimation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [engineers, setEngineers] = useState([]);
  const [selectedEngineer, setSelectedEngineer] = useState("");
  const [timerMinutes, setTimerMinutes] = useState("30");
  const [granting, setGranting] = useState(false);
  const [grantMsg, setGrantMsg] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const timerRef = useRef(null);

  const fetchEstimation = useCallback(async () => {
    try {
      setEstimation(await apiClient.get(`/estimations/${id}`));
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEstimation();
    if (admin) apiClient.get("/engineers").then(setEngineers).catch(() => {});
  }, [fetchEstimation, admin]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    const editUntil = estimation?.editable_until;
    if (!editUntil) {
      setTimeLeft(0);
      return;
    }
    const tick = () => {
      const remaining = Math.floor((new Date(editUntil).getTime() - Date.now()) / 1000);
      setTimeLeft(remaining > 0 ? remaining : 0);
      if (remaining <= 0 && timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => timerRef.current && clearInterval(timerRef.current);
  }, [estimation?.editable_until]);

  const canEdit = () => {
    if (!estimation) return false;
    if (admin) return true;
    return estimation.is_editable && estimation.editable_by === user?.id && timeLeft > 0;
  };

  const formatTimer = (secs) => `${Math.floor(secs / 60)}:${(secs % 60).toString().padStart(2, "0")}`;

  const handleGrantEdit = async () => {
    if (!selectedEngineer || !timerMinutes) return;
    setGranting(true);
    setGrantMsg("");
    try {
      await apiClient.post(`/estimations/${id}/grant-edit`, {
        engineer_id: selectedEngineer,
        duration_minutes: parseInt(timerMinutes, 10),
      });
      setGrantMsg(t("edit_granted_success"));
      await fetchEstimation();
    } catch (e) {
      setGrantMsg(e?.message || "Error granting access");
    } finally {
      setGranting(false);
    }
  };

  const doDelete = async () => {
    setShowConfirm(false);
    setDeleting(true);
    setErrorMsg("");
    try {
      await apiClient.del(`/estimations/${id}`);
      navigate(-1);
    } catch (e) {
      setErrorMsg(e.message || t("failed_delete"));
      setDeleting(false);
    }
  };

  if (loading) return <PageLoader />;
  if (!estimation) return <EmptyState icon={FileText} title={t("estimation_not_found")} />;

  const accent = CategoryColors[estimation.category] || "#2563EB";
  const rowsList = estimation.rows || [];
  const retPct = estimation.retention_pct ?? 10;
  const cur = normalizeCurrency(estimation.currency);
  const subtotal = estimation.subtotal ?? estimation.final_total ?? 0;
  const prevRet = estimation.previous_retention ?? 0;
  const currentEst = cleanNum(subtotal * (1 - retPct / 100));
  const currentRet = cleanNum(subtotal * (retPct / 100));
  const finalSummary = cleanNum(currentEst + prevRet);

  return (
    <div className="mx-auto max-w-4xl pb-10">
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900">
        <ArrowLeft className={`h-4 w-4 ${isRTL ? "rotate-180" : ""}`} /> {t("back")}
      </button>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-5" style={{ backgroundColor: `${accent}0d`, borderColor: `${accent}33` }}>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: accent }}>{t(estimation.category)}</div>
          <h1 className="text-xl font-bold text-slate-900">
            {t("unit")} {estimation.unit_number} · #{estimation.estimation_number}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {canEdit() ? (
            <button
              data-testid="edit-estimation-btn"
              onClick={() => navigate(`/estimation/${estimation.category}?unit_number=${estimation.unit_number}&edit_id=${id}`)}
              className="flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-white"
              style={{ backgroundColor: accent }}
            >
              <Pencil className="h-4 w-4" /> {t("edit_estimation")}
            </button>
          ) : null}
          {estimation.excel_url ? (
            <a href={estimation.excel_url} target="_blank" rel="noreferrer" data-testid="download-excel-btn" className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-emerald-600">
              <Download className="h-4 w-4" /> {t("download_excel")}
            </a>
          ) : null}
          {admin ? (
            <button data-testid="delete-estimation-btn" onClick={() => setShowConfirm(true)} className="flex h-10 w-10 items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-500">
              <Trash2 className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>

      {errorMsg ? <div className="mb-4 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600"><AlertCircle className="h-4 w-4" /> {errorMsg}</div> : null}

      {estimation.is_mechanic_progress ? (
        <div className="mb-4 flex items-center gap-2 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
          <Lock className="h-4 w-4" /> {t("mechanic_locked_note")}
        </div>
      ) : null}

      <Card className="mb-4">
        <div className="mb-3 flex items-center gap-2"><FileText className="h-5 w-5" style={{ color: accent }} /><h3 className="text-base font-semibold text-slate-900">{t("top_info")}</h3></div>
        <Field label={t("estimation_number")} value={`#${estimation.estimation_number || "-"}`} />
        {estimation.subject ? <Field label={t("subject")} value={estimation.subject} /> : null}
        <Field label={t("date_label")} value={estimation.date || "-"} />
        <Field label={t("category_label")} value={t(estimation.category)} />
      </Card>

      {estimation.contractor_name || estimation.contractor_phone ? (
        <Card className="mb-4">
          <div className="mb-3 flex items-center gap-2"><Building className="h-5 w-5" style={{ color: accent }} /><h3 className="text-base font-semibold text-slate-900">{t("contractor_name")}</h3></div>
          {estimation.contractor_name ? <Field label={t("contractor_name")} value={estimation.contractor_name} /> : null}
          {estimation.contractor_phone ? <Field label={t("contractor_phone")} value={estimation.contractor_phone} /> : null}
        </Card>
      ) : null}

      <Card className="mb-4">
        <div className="mb-3 flex items-center gap-2"><User className="h-5 w-5" style={{ color: accent }} /><h3 className="text-base font-semibold text-slate-900">{t("engineer_name_label")}</h3></div>
        <Field label={t("engineer_name_label")} value={estimation.engineer_name || "-"} />
      </Card>

      {rowsList.length > 0 ? (
        <Card className="mb-4">
          <div className="mb-3 flex items-center gap-2"><Grid3x3 className="h-5 w-5" style={{ color: accent }} /><h3 className="text-base font-semibold text-slate-900">{t("table_entries")} ({rowsList.length})</h3></div>
          <div className="space-y-3">
            {rowsList.map((row, idx) => (
              <div key={idx} data-testid={`detail-row-${idx}`} className={`rounded-xl border border-slate-100 p-3 ${idx % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: accent }}>{row.index || idx + 1}</span>
                  <span className="text-sm font-semibold text-slate-800">
                    {estimation.category === "electrical" ? translateKey(row.electrical_type || row.main_type, language) : translateKey(row.main_type, language)}
                  </span>
                </div>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {row.home_type ? <Badge>{t("home_type")}: {row.home_type}</Badge> : null}
                  {row.house_number ? <Badge>#{row.house_number}</Badge> : null}
                  {row.block_number ? <Badge color="#2563EB">{row.block_number}</Badge> : null}
                  {row.percentage ? <Badge color="#059669">{row.percentage}%</Badge> : null}
                  {row.sub_type ? <Badge>{translateKey(row.sub_type, language)}</Badge> : null}
                  {row.sub_sub_type ? <Badge color="#7C3AED">{translateKey(row.sub_sub_type, language)}</Badge> : null}
                </div>
                {row.description ? <p className="mb-2 text-sm text-slate-600">{row.description}</p> : null}
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {row.piece_count ? <ValItem label={t("piece_count")} value={row.piece_count} /> : null}
                  <ValItem label={t("quantity")} value={`${row.quantity || "-"} ${row.unit || ""}`} />
                  <ValItem label={t("unit_price")} value={row.unit_price ? fmtMoney(row.unit_price, cur) : "-"} />
                  <ValItem label={t("total_price")} value={row.total_price ? fmtMoney(row.total_price, cur) : "-"} highlight />
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      <Card className="mb-4">
        <div className="mb-3 flex items-center gap-2"><Calculator className="h-5 w-5" style={{ color: accent }} /><h3 className="text-base font-semibold text-slate-900">{t("totals_section")}</h3></div>
        <Field label={t("total_work")} value={fmtMoney(subtotal, cur)} />
        <Field label={t("retention_percentage")} value={`${retPct}%`} />
        <div className="flex items-center justify-between py-2.5"><span className="text-sm text-slate-500">{language === "ku" ? `پێشینە ئێستا (${100 - retPct}%)` : `Current Estimation (${100 - retPct}%)`}</span><span className="text-sm font-semibold text-emerald-600">{fmtMoney(currentEst, cur)}</span></div>
        <div className="flex items-center justify-between py-2.5"><span className="text-sm text-slate-500">{language === "ku" ? `تەئمینات ڤێلا (${retPct}%)` : `Current Retention (${retPct}%)`}</span><span className="text-sm font-semibold text-red-500">{fmtMoney(currentRet, cur)}</span></div>
        <Field label={t("previous_retention_10")} value={fmtMoney(prevRet, cur)} />
        <div className="mt-2 flex items-center justify-between border-t border-slate-100 pt-3"><span className="text-base font-bold text-slate-900">{t("final_summary")}</span><span className="text-xl font-bold text-emerald-600">{fmtMoney(finalSummary, cur)}</span></div>
      </Card>

      {/* Edit permission control (admin) */}
      {admin ? (
        <Card className="mb-4">
          <div className="mb-4 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[var(--brand)]" /><h3 className="text-base font-semibold text-slate-900">{t("edit_permission_control")}</h3></div>
          {estimation.is_editable && estimation.editable_until && timeLeft > 0 ? (
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              <Timer className="h-4 w-4" /> {t("edit_active_until")}: {new Date(estimation.editable_until).toLocaleTimeString()} ({formatTimer(timeLeft)} {t("time_remaining").toLowerCase()})
            </div>
          ) : (
            <div className="mb-4 flex items-center gap-2 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500"><Lock className="h-4 w-4" /> {t("edit_locked")}</div>
          )}
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("select_engineer")}</label>
          <select data-testid="engineer-select" value={selectedEngineer} onChange={(e) => setSelectedEngineer(e.target.value)} className="mb-4 h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none">
            <option value="">{t("select_engineer")}</option>
            {engineers.map((eng) => (
              <option key={eng.id} value={eng.id}>{eng.name} ({t(eng.role)})</option>
            ))}
          </select>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{t("timer_duration")}</label>
          <input value={timerMinutes} onChange={(e) => setTimerMinutes(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder="30" className="mb-4 h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none" />
          {grantMsg ? <p className="mb-3 text-sm text-emerald-600">{grantMsg}</p> : null}
          <button data-testid="grant-edit-btn" onClick={handleGrantEdit} disabled={granting || !selectedEngineer} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[var(--brand)] text-sm font-semibold text-white disabled:opacity-60">
            {granting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} {t("grant_edit_access")}
          </button>
        </Card>
      ) : null}

      {/* Change log */}
      {estimation.version_number > 1 || (estimation.change_log && estimation.change_log.length > 0) ? (
        <Card className="mb-4">
          <div className="mb-3 flex items-center gap-2"><History className="h-5 w-5 text-amber-500" /><h3 className="text-base font-semibold text-slate-900">{t("change_log")}</h3>{estimation.version_number ? <span className="ms-auto text-xs text-slate-400">{t("version")}: {estimation.version_number}</span> : null}</div>
          {estimation.change_log && estimation.change_log.length > 0 ? (
            <div className="space-y-2">
              {estimation.change_log.map((ch, idx) => (
                <div key={idx} className="border-b border-slate-50 py-2 text-sm text-slate-600 last:border-0">
                  <span className="font-bold text-slate-800">{ch.field_name}</span> {t("changed_from")} <span className="text-red-500">{ch.old_value || "-"}</span> {t("changed_to")} <span className="text-emerald-600">{ch.new_value || "-"}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400">{t("no_changes")}</p>
          )}
        </Card>
      ) : null}

      {showConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between"><h3 className="text-lg font-bold text-slate-900">{t("confirm_delete")}</h3><button onClick={() => setShowConfirm(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <p className="mb-6 text-sm text-slate-500">{t("confirm_delete_msg")}</p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(false)} className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-slate-600">{t("cancel")}</button>
              <button data-testid="confirm-delete-btn" onClick={doDelete} disabled={deleting} className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-red-500 py-2.5 text-sm font-semibold text-white disabled:opacity-70">
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("delete")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const Badge = ({ children, color = "#64748B" }) => (
  <span className="rounded-md px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: `${color}15`, color }}>{children}</span>
);
const ValItem = ({ label, value, highlight }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-sm font-semibold ${highlight ? "text-emerald-600" : "text-slate-800"}`}>{value}</div>
  </div>
);
